import { TableDetailsTabType, getWebPath } from '@doss/common-all'
import { orderBy } from 'lodash'
import { useMemo } from 'react'
import { RxTable } from 'react-icons/rx'
import { useNavigate, useSearchParams } from 'react-router'
import { cn } from '~/@/lib/utils'
import {
  BreadcrumbObjectType,
  ChartType,
  useGetDetailedTableQuery,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { retrieveLastViewedPivotTable, retrieveLastViewedTab } from '~/common/utils/local-storage'
import { PivotTableIcon } from '../pivot-table/icons/PivotTableIcon'

/**
 * Renders the tabs to navigate between table tabs and table pivot tables
 * This component works independently of the TableDetailsProvider context
 */
export function TableNav({ tableId }: { tableId: string }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTypedTranslation()
  const { user, permissionsManager } = useGlobalContext()

  const { data: tableData } = useGetDetailedTableQuery({
    variables: { tableId },
  })

  const canConfigurePivotTables = permissionsManager.canConfigurePivotTables(tableId)

  const hasPivotTables = useMemo(() => {
    const pivotTables =
      tableData?.table.charts.filter((chart) => chart.chartType === ChartType.PIVOT_TABLE) ?? []
    return pivotTables.length > 0
  }, [tableData])

  // Determine active tab type from URL, defaulting to TABLE_TABS
  const activeTabType =
    (searchParams.get('tab') as TableDetailsTabType | null) ?? TableDetailsTabType.TABLE_TABS

  // Check if we're in pivot view or table view mode
  const isPivotView = activeTabType === TableDetailsTabType.PIVOT_TABLES
  const isTableView = activeTabType === TableDetailsTabType.TABLE_TABS

  // Get last viewed items from local storage for navigation
  const lastViewedTabId = user ? retrieveLastViewedTab(user.id, tableId) : undefined
  const lastViewedPivotTableId = user ? retrieveLastViewedPivotTable(user.id, tableId) : undefined

  // Handle tab change - uses router.replace with shallow: true
  const handleTabChange = (tabType: TableDetailsTabType) => {
    let path = ''

    if (tabType === TableDetailsTabType.TABLE_TABS) {
      // Get current view ID or use last viewed tab ID
      // If we have no IDs, we need to fetch the first available tab
      const tabId =
        searchParams.get('view') ||
        lastViewedTabId ||
        (tableData?.table.tableTabs && tableData.table.tableTabs.length > 0
          ? orderBy(tableData.table.tableTabs, 'order')[0]?.id
          : undefined)

      path = getWebPath({
        objectId: tableId,
        objectType: BreadcrumbObjectType.TABLE,
        tableParams: {
          tab: TableDetailsTabType.TABLE_TABS,
          id: tabId || undefined,
        },
      })
    } else {
      // Get current pivot ID or use last viewed pivot ID
      // If we have no IDs, we need to fetch the first available pivot table
      const pivotTables =
        tableData?.table.charts.filter((chart) => chart.chartType === ChartType.PIVOT_TABLE) || []

      const pivotId =
        searchParams.get('pivot') ||
        lastViewedPivotTableId ||
        (pivotTables.length > 0 ? pivotTables[0]?.id : undefined)

      path = getWebPath({
        objectId: tableId,
        objectType: BreadcrumbObjectType.TABLE,
        tableParams: {
          tab: TableDetailsTabType.PIVOT_TABLES,
          id: pivotId || undefined,
        },
      })
    }

    // Only proceed if we have a valid path with an ID
    if (path) {
      navigate(path, { replace: true })
    }
  }

  return (
    <div className="transition-color flex cursor-pointer items-center gap-4 px-5 text-xs text-gray-8 duration-300">
      <button
        className={cn(
          'flex items-center h-full border-b-2 px-2 gap-2',
          isTableView ? 'border-gray-10 text-gray-10' : 'border-transparent hover:text-gray-8',
        )}
        onClick={() => handleTabChange(TableDetailsTabType.TABLE_TABS)}
        type="button"
      >
        <RxTable className={cn('size-3', isTableView ? 'text-gray-10' : 'text-gray-8')} />
        {t('table.views')}
      </button>
      {canConfigurePivotTables && hasPivotTables && (
        <button
          className={cn(
            'flex items-center h-full border-b-2 px-2 gap-2',
            isPivotView ? 'border-gray-10 text-gray-10' : 'border-transparent hover:text-gray-8',
          )}
          onClick={() => handleTabChange(TableDetailsTabType.PIVOT_TABLES)}
          type="button"
        >
          <PivotTableIcon className={cn('size-3', isPivotView ? 'text-gray-10' : 'text-gray-8')} />
          {t('table.pivot_tables')}
        </button>
      )}
    </div>
  )
}
